// Turns a raw backend error string into copy that is safe to show an operator.
//
// The group/engine services leak internal shapes into their error messages:
// raw field names ("Verification national_id has not passed"), bare UUIDs
// ("OTP for 54c86daa-... is not valid") and other backend phrasing. This maps
// the known shapes to plain copy and, as a backstop, strips any UUID and
// humanises snake_case field names so nothing internal reaches the screen.
//
// It is intentionally conservative: a message it does not recognise and that
// contains no UUID or known field token is returned unchanged, so ordinary
// backend messages are not mangled. (BUG-10, admin side.)

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

// Internal field tokens -> the label a person should see.
const FIELD_LABELS = {
  national_id: 'National ID',
  kra_pin: 'KRA PIN',
  msisdn: 'mobile number',
  mobile: 'mobile number',
  phone_number: 'mobile number',
  date_of_birth: 'date of birth',
  dob: 'date of birth',
  email: 'email address',
  first_name: 'first name',
  last_name: 'surname',
  surname: 'surname'
}

function labelFor(field) {
  const key = String(field).toLowerCase()
  return FIELD_LABELS[key] || String(field).replace(/_/g, ' ')
}

// Known message shapes, matched before the generic sanitiser runs.
const RULES = [
  {
    // "Verification national_id has not passed"
    pattern: /^verification\s+(\w+)\s+has not passed\.?$/i,
    copy(match) {
      return `${capitalise(labelFor(match[1]))} verification did not pass.`
    }
  },
  {
    // "OTP for 54c86daa-... is not valid"
    pattern: /^otp for .+ is not valid\.?$/i,
    copy() {
      return (
        'That one-time PIN is invalid or has expired. Please request a ' +
        'new one.'
      )
    }
  },
  {
    // "The national ID 234567890 is not valid for this partner."
    pattern: /national id .* is not valid for this partner/i,
    copy() {
      return 'That National ID is not valid for this partner.'
    }
  }
]

function capitalise(text) {
  if (!text) {
    return text
  }
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Replaces UUIDs and any bare snake_case field token with human copy. Returns
// the string unchanged when there is nothing internal to remove.
function sanitise(message) {
  let out = message.replace(UUID_RE, 'this request')
  Object.keys(FIELD_LABELS).forEach(function(token) {
    const wordRe = new RegExp(`\\b${token}\\b`, 'gi')
    out = out.replace(wordRe, FIELD_LABELS[token])
  })
  return out
}

export function humaniseError(message) {
  if (!message || typeof message !== 'string') {
    return 'An unexpected error occurred'
  }
  const trimmed = message.trim()
  for (let i = 0; i < RULES.length; i++) {
    const match = trimmed.match(RULES[i].pattern)
    if (match) {
      return RULES[i].copy(match)
    }
  }
  return sanitise(trimmed)
}
